import React from "react";

import { formatDateTime } from "../../../../utils/datetime";
import {
  CardCta,
  CardEmptyState,
  CardNeutralState,
  ClipboardIcon,
  OverviewCard,
  StatusPill,
} from "./overviewUi";

const STATUS_META = {
  todo: { label: "Offen", tone: "sky" },
  in_progress: { label: "In Arbeit", tone: "amber" },
  blocked: { label: "Blockiert", tone: "rose" },
};

const PRIORITY_LABEL = {
  low: "Niedrig",
  medium: "Mittel",
  high: "Hoch",
  urgent: "Dringend",
};

/**
 * "Offene Aufgaben" — the few that are still to be done, never the whole table.
 *
 * The count is the list response's `total`, not `items.length`: a card that says
 * "4" while the server holds forty is a lie the reader has no way to detect. The
 * rows shown are capped by the caller; the CTA opens the tab that has all of
 * them.
 */
export default function OpenTasksCard({ tasks, error, onOpen }) {
  const items = tasks?.items || [];
  const total = tasks?.total ?? 0;

  return (
    <OverviewCard
      title="Offene Aufgaben"
      icon={<ClipboardIcon />}
      accent="blue"
      testId="overview-tasks-card"
      action={
        !error && total > 0 ? (
          <StatusPill tone="sky" testId="overview-tasks-count">
            {total}
          </StatusPill>
        ) : null
      }
      footer={
        onOpen ? (
          <CardCta onClick={onOpen} testId="overview-tasks-cta">
            Alle Aufgaben anzeigen
          </CardCta>
        ) : null
      }
    >
      {error ? (
        <CardNeutralState
          testId="overview-tasks-error"
          text="Aufgaben konnten nicht geladen werden."
        />
      ) : total === 0 ? (
        <CardEmptyState
          testId="overview-tasks-empty"
          title="Keine offenen Aufgaben"
          hint="Alle Aufgaben sind erledigt."
        />
      ) : (
        <>
          <ul className="m-0 list-none divide-y divide-border p-0">
            {items.map((task) => {
              const meta = STATUS_META[String(task.status || "").toLowerCase()];
              return (
                <li
                  key={task.id}
                  className="flex items-start justify-between gap-3 py-2.5"
                  data-testid="overview-task-row"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-[13.5px] text-card-foreground">
                      {task.title}
                    </span>
                    <span className="mt-0.5 block text-[11.5px] text-muted-foreground">
                      {task.due_at
                        ? `Fällig ${formatDateTime(task.due_at)}`
                        : PRIORITY_LABEL[
                            String(task.priority || "").toLowerCase()
                          ] || "Ohne Termin"}
                    </span>
                  </span>
                  {meta ? (
                    <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {total > items.length ? (
            <p className="m-0 pt-3 text-[11.5px] text-muted-foreground">
              {`${total - items.length} weitere offene Aufgaben`}
            </p>
          ) : null}
        </>
      )}
    </OverviewCard>
  );
}
